import React from "react";
import { useGst } from "@/context/GstContext";
import { useLang } from "@/context/LangContext";
import { CheckCircle2, Circle } from "lucide-react";

/**
 * Highly visible GST-registration toggle.
 * Variant 'pill': compact, used in topbars / sidebars.
 * Variant 'banner': full-width, used at the top of calculator pages.
 */
export default function GstToggle({ variant = "pill", className = "" }) {
  const { gstRegistered, toggleGst } = useGst();
  const { t } = useLang();

  if (variant === "banner") {
    return (
      <div
        className={`border border-[var(--border)] bg-[var(--surface)] p-3 sm:p-4 flex flex-wrap items-center justify-between gap-3 ${className}`}
        data-testid="gst-toggle-banner"
      >
        <div className="flex items-center gap-3 min-w-0">
          {gstRegistered ? (
            <CheckCircle2 size={18} className="text-[var(--primary)] shrink-0" />
          ) : (
            <Circle size={18} className="text-zinc-400 shrink-0" />
          )}
          <div className="min-w-0">
            <div className="font-mono text-xs uppercase tracking-wider text-zinc-300">
              {gstRegistered
                ? t("budget.gst_on_label", "GST registered")
                : t("budget.gst_off_label", "Not GST registered")}
            </div>
            <div className="text-[11px] text-zinc-500 mt-0.5">
              {gstRegistered
                ? t("budget.gst_on_hint", "Calculations treat income as ex-GST. GST passes through to IRD.")
                : t("budget.gst_off_hint", "Under the $60k threshold. No GST on invoices.")}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={toggleGst}
          className={`flex items-center gap-2 px-3 py-2 border font-mono text-[11px] uppercase tracking-wider transition-colors shrink-0 ${
            gstRegistered
              ? "bg-[var(--primary)] text-[var(--on-primary)] border-[var(--primary)]"
              : "border-[var(--border)] text-zinc-300 hover:border-[var(--primary)]"
          }`}
          aria-pressed={gstRegistered}
          data-testid="gst-toggle-btn"
        >
          {gstRegistered
            ? t("budget.gst_yes_btn", "Yes · I'm registered")
            : t("budget.gst_no_btn", "No · turn on")}
        </button>
      </div>
    );
  }

  // pill variant (topbar)
  return (
    <button
      type="button"
      onClick={toggleGst}
      className={`flex items-center gap-1.5 px-2.5 py-1.5 border font-mono text-[10px] uppercase tracking-wider transition-colors ${
        gstRegistered
          ? "bg-[var(--primary)]/15 text-[var(--primary)] border-[var(--primary)]/50"
          : "border-[var(--border)] text-zinc-400 hover:border-[var(--primary)]"
      } ${className}`}
      title={
        gstRegistered
          ? "GST-registered · click to switch to not registered"
          : "Not GST-registered · click to switch"
      }
      aria-pressed={gstRegistered}
      data-testid="gst-toggle-pill"
    >
      {gstRegistered ? (
        <CheckCircle2 size={12} strokeWidth={2.5} />
      ) : (
        <Circle size={12} strokeWidth={2.5} />
      )}
      GST {gstRegistered ? "ON" : "OFF"}
    </button>
  );
}
